import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY as C } from "../constants/company";
import { LangSwitch } from "./Header";

const KEY = "goox_age_verified";
const ease = [0.16, 1, 0.3, 1];

export const AgeGate = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(() => localStorage.getItem(KEY) !== "1");

  const enter = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };
  const leave = () => {
    window.location.href = "https://www.google.com";
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="age-gate"
          role="dialog"
          aria-modal="true"
          aria-labelledby="age-gate-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.35 } }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-night/90 px-4 backdrop-blur-xl"
          data-lenis-prevent
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.7, ease }}
            className="hero-card grain relative w-full max-w-lg overflow-hidden rounded-[2rem] border border-white/70 p-8 shadow-2xl sm:p-10"
          >
            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="font-display text-2xl font-extrabold tracking-tight text-ink">
                  GOOX<span className="ml-0.5 inline-block h-2 w-2 rounded-full bg-gradient-to-br from-violet-500 to-pink-500" />
                </p>
                <LangSwitch compact />
              </div>
              <div className="mt-8 flex items-center gap-4">
                <span data-testid="age-gate-badge" className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-[3px] border-ink font-display text-xl font-extrabold text-ink">18+</span>
                <div>
                  <p className="eyebrow text-violet-700">{t.ageGate.eyebrow}</p>
                  <h2 id="age-gate-title" data-testid="age-gate-title" className="font-display mt-1 text-2xl font-extrabold leading-tight tracking-tight text-ink sm:text-3xl">{t.ageGate.title}</h2>
                </div>
              </div>
              <p className="mt-6 text-sm leading-relaxed text-ink-2 sm:text-base">{t.ageGate.body}</p>
              <p className="mt-3 text-xs leading-relaxed text-mist">
                {t.ageGate.legal}{" "}
                <Link to="/legal/terms" onClick={enter} className="font-semibold text-violet-700 hover:underline">{t.legalTitles.terms}</Link>,{" "}
                <Link to="/legal/privacy" onClick={enter} className="font-semibold text-violet-700 hover:underline">{t.legalTitles.privacy}</Link>{" "}
                {t.ageGate.and}{" "}
                <Link to="/legal/age-safety" onClick={enter} className="font-semibold text-violet-700 hover:underline">{t.legalTitles["age-safety"]}</Link>.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button data-testid="age-gate-enter-button" onClick={enter} className="pill btn-primary flex-1 px-6 py-3.5 text-sm font-semibold">
                  <ShieldAlert size={16} className="mr-2 inline-block" />
                  {t.ageGate.enter}
                </button>
                <button data-testid="age-gate-exit-button" onClick={leave} className="pill btn-ghost flex-1 px-6 py-3.5 text-sm font-semibold">
                  {t.ageGate.exit}
                </button>
              </div>
              <p className="eyebrow mt-6 text-center text-[0.58rem] text-mist">{t.ageGate.operated} {C.legalName} · {C.country}</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
